/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

/**
 * PairingChannel — TypeScript wrapper around the fxa-pairing-channel npm
 * package.  Handles PSK-encrypted WebSocket communication between the
 * supplicant and the channel server (which relays messages to/from the
 * authority).
 *
 * Ported from: fxa-content-server/app/scripts/lib/pairing-channel-client.js
 */

import sentryMetrics from '@fxa/shared/sentry-browser';
import { RemoteMetadata } from '../types';
import UAParser from 'ua-parser-js';
import { toGenericOSName } from '../utilities';
import { base64urlToBytes, bytesToBase64url } from '../base64url';
import { ResumableWebSocket } from './resumable-websocket';

// Types

export type PairingChannelMessage = {
  message: string;
  data?: Record<string, unknown>;
};

export type PairingChannelRemoteMetadata = {
  city?: string;
  country?: string;
  region?: string;
  ua?: string;
  ipAddress?: string;
};

/**
 * Derive the device details shown to the user from the channel server's
 * `sender` envelope. Shared by both sides of the flow: the supplicant reads it
 * off `pair:auth:metadata`, the authority off `pair:supp:request`.
 */
export function toRemoteMetadata(
  remote: PairingChannelRemoteMetadata,
  deviceName?: string
): RemoteMetadata {
  const parser = new UAParser(remote.ua || '');
  const browser = parser.getBrowser();
  const os = parser.getOS();

  return {
    city: remote.city,
    country: remote.country,
    region: remote.region,
    ipAddress: remote.ipAddress || '',
    deviceFamily: browser.name || 'Unknown',
    deviceOS: toGenericOSName(os.name || ''),
    deviceName: deviceName || browser.name || 'Unknown',
  };
}

export type PairingChannelIncomingMessage = {
  remoteMetaData: PairingChannelRemoteMetadata;
  deviceName?: string;
  email?: string;
  code?: string;
  state?: string;
};

export type PairingChannelV2SupplicantRequestMessage = {
  remoteMetaData: PairingChannelRemoteMetadata;
  client_id: string;
  code_challenge: string;
  code_challenge_method: string;
  keys_jwk: string;
  scope: string;
  state: string;
};

// Error definitions matching Backbone's pairing-channel-client-errors
export const PairingChannelErrors = {
  UNEXPECTED_ERROR: { errno: 999, message: 'Unexpected error' },
  INVALID_CONFIGURATION: {
    errno: 1000,
    message: 'Invalid channel server configuration',
  },
  ALREADY_CONNECTED: {
    errno: 1001,
    message: 'Already connected to channel server',
  },
  NOT_CONNECTED: {
    errno: 1002,
    message: 'Not connected to channel server',
  },
  INVALID_MESSAGE: {
    errno: 1003,
    message: 'Invalid message from the remote device',
  },
  INVALID_OUTBOUND_MESSAGE: {
    errno: 1004,
    message: 'Sending a malformed message',
  },
  CHANNEL_ID_MISMATCH: {
    errno: 1005,
    message: 'Error pairing to remote device',
  },
  CONNECTION_CLOSED: {
    errno: 1006,
    message: 'Connection to remote device closed, please try again',
  },
} as const;

export type PairingChannelErrorType = keyof typeof PairingChannelErrors;

/**
 * fxa-pairing-channel opens its socket with the global WebSocket inside
 * create() and connect(), and takes no socket option, so swap it for that call.
 */
function withResumableWebSocket<T>(start: () => Promise<T>): {
  result: Promise<T>;
  socket?: ResumableWebSocket;
} {
  const NativeWebSocket = window.WebSocket;
  let socket: ResumableWebSocket | undefined;
  window.WebSocket = class extends ResumableWebSocket {
    constructor(url: string) {
      super(url, NativeWebSocket);
      socket = this;
    }
  } as unknown as typeof WebSocket;
  try {
    const result = start();
    return { result, socket };
  } finally {
    window.WebSocket = NativeWebSocket;
  }
}

export class PairingChannelError extends Error {
  readonly errno: number;
  constructor(type: PairingChannelErrorType) {
    const def = PairingChannelErrors[type];
    super(def.message);
    this.name = 'PairingChannelError';
    this.errno = def.errno;
  }
}

/**
 * The channel server drops the socket rather than answering when the channel is
 * gone — expired, already consumed by a completed pairing, or at its two-client
 * limit. The same rejection covers a socket lost mid-handshake, which the
 * supplicant's mobile webview hits on any network change or backgrounding.
 * fxa-pairing-channel raises a bare Error for all of it, so the message is the
 * only signal to match on.
 */
const CHANNEL_CLOSED_MESSAGE = 'WebSocket unexpectedly closed';

function isChannelClosedError(err: unknown): boolean {
  return err instanceof Error && err.message === CHANNEL_CLOSED_MESSAGE;
}

/**
 * EventTarget-based client that wraps fxa-pairing-channel for PSK-encrypted
 * WebSocket communication with the channel server.
 *
 * Events emitted:
 *  - `connected`                   — WebSocket open
 *  - `close`                       — WebSocket closed
 *  - `error`                       — error (detail: PairingChannelError)
 *  - `remote:<message_name>`       — incoming message from remote peer
 *                                    (detail: PairingChannelIncomingMessage)
 */
type PairingChannelSocket = {
  send(data: unknown): Promise<void>;
  close(): Promise<void>;
  addEventListener(type: string, listener: EventListener): void;
  removeEventListener(type: string, listener: EventListener): void;
  _channelId?: string;
  _channelKey?: Uint8Array;
  /**
   * fxa-pairing-channel's own handle on the socket. It is nulled out when the
   * socket goes away, and the package's `close()` dereferences it unguarded.
   */
  _connection?: unknown;
  /** True once fxa-pairing-channel has torn the WebSocket down. */
  readonly closed?: boolean;
};

export class PairingChannelClient extends EventTarget {
  private channel: PairingChannelSocket | null = null;
  private socket?: ResumableWebSocket;
  private _opening = false;
  private unloading = false;

  /**
   * Flag for if the page is in a 'unloading' state. This is based on the
   * page's lifecycle events.
   */
  get isUnloading(): boolean {
    return this.unloading;
  }

  get channelKey() {
    if (this.channel?._channelKey) {
      return bytesToBase64url(this.channel._channelKey);
    }
    return null;
  }

  get channelId() {
    if (this.channel?._channelId) {
      return this.channel._channelId;
    }
    return null;
  }

  /**
   * Creates a new pairing channel. Once opened, a channelId and channelKey
   * should be populated on this instance.
   *
   * @param channelServerUri The channel server to connect to.
   */
  async create(channelServerUri: string): Promise<void> {
    if (this.channel || this._opening) {
      throw new PairingChannelError('ALREADY_CONNECTED');
    }

    if (!channelServerUri) {
      throw new PairingChannelError('INVALID_CONFIGURATION');
    }

    this._opening = true;
    this.watchPageLifecycle();
    try {
      const { PairingChannel } = await import(
        /* webpackChunkName: "fxaPairingChannel" */
        'fxa-pairing-channel/dist/FxAccountsPairingChannel.babel.umd.js'
      );
      const { result, socket } = withResumableWebSocket(() =>
        PairingChannel.create(channelServerUri)
      );
      this.attachSocket(socket);
      const channel = await result;
      this.channel = channel;

      this.attachChannel(channel);

      this.dispatchEvent(new CustomEvent('connected'));
    } catch (err) {
      if (!this.unloading) {
        this.captureWithCloseReason(err);
        this.dispatchEvent(new CustomEvent('error', { detail: err }));
      }
      this.detachSocket();

      // Rethrow so the caller can tell a failed create from a successful one.
      // Without this the authority resolves as if it had a channel and encodes
      // `channel_id=null` into a QR code the supplicant cannot join.
      throw err;
    } finally {
      this._opening = false;
    }
  }

  /**
   * Opens an existing pairing channel connection
   * @param channelServerUri Channel server to connect to
   * @param channelId Id of channel to open
   * @param channelKey Key for channel
   */
  async open(
    channelServerUri: string,
    channelId: string,
    channelKey: string
  ): Promise<void> {
    if (this.channel || this._opening) {
      throw new PairingChannelError('ALREADY_CONNECTED');
    }

    if (!channelServerUri || !channelId || !channelKey) {
      throw new PairingChannelError('INVALID_CONFIGURATION');
    }

    this._opening = true;
    this.watchPageLifecycle();
    try {
      const psk = base64urlToBytes(channelKey);

      // Dynamic import — fxa-pairing-channel UMD bundle exposes PairingChannel.connect()
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore — no types for this legacy UMD package
      const FxAccountsPairingChannel = await import(
        /* webpackChunkName: "fxaPairingChannel" */
        'fxa-pairing-channel/dist/FxAccountsPairingChannel.babel.umd.js'
      );

      const { result, socket } = withResumableWebSocket(() =>
        FxAccountsPairingChannel.PairingChannel.connect(
          channelServerUri,
          channelId,
          psk
        )
      );
      this.attachSocket(socket);
      const channel = await result;

      this.channel = channel;

      this.attachChannel(channel);

      this.dispatchEvent(new CustomEvent('connected'));
    } catch (err) {
      // A dropped socket is the channel server's normal answer for a channel it
      // will not serve, so it carries no signal worth an exception report. The
      // console breadcrumb keeps it visible in the trail of any real error
      // that follows.
      const closed = isChannelClosedError(err);
      if (this.unloading) {
        // The browser dropped it on the page's way out; nothing to report.
      } else if (closed) {
        console.warn('Pairing channel closed before it opened', channelId);
      } else {
        this.captureWithCloseReason(err);
      }

      // Report the closed socket under its own errno so a caller reading the
      // error sees the same 1006 a mid-flow close produces.
      const detail = closed
        ? new PairingChannelError('CONNECTION_CLOSED')
        : err;
      if (!this.unloading) {
        this.dispatchEvent(new CustomEvent('error', { detail }));
      }

      this.detachSocket();

      // Rejecting is what lets the caller drop this instance; resolving leaves
      // a client that looks live and blocks every later reopen.
      throw detail;
    } finally {
      this._opening = false;
    }
  }

  async send(
    message: string,
    data: Record<string, unknown> = {}
  ): Promise<void> {
    if (!this.channel) {
      console.warn('No pairing channel!');
      throw new PairingChannelError('NOT_CONNECTED');
    }
    if (this.channel.closed) {
      console.warn('Pairing channel is closed!');
      throw new PairingChannelError('CONNECTION_CLOSED');
    }
    if (!message) {
      console.warn('No message!');
      throw new PairingChannelError('INVALID_OUTBOUND_MESSAGE');
    }
    console.info('Sending pairing message', message);
    await this.channel.send({ message, data });
  }

  async close(): Promise<void> {
    this.unwatchPageLifecycle();
    this.detachSocket();
    if (!this.channel) {
      return; // Already closed or never opened
    }
    const ch = this.channel;
    this.channel = null;
    this.removeChannelListeners(ch);

    // An absent `_connection` is not the same signal as a null one.
    // Only null means the channel tore down the socket.
    if (ch._connection === null) {
      return;
    }

    try {
      console.info('Closing channel ', ch._channelId);
      await ch.close();
    } catch (err) {
      console.error('Error closing channel', err);
      sentryMetrics.captureException(err);
    }
  }

  get isConnected(): boolean {
    return this.channel !== null;
  }

  private handleMessage = (event: Event) => {
    try {
      const detail = (event as CustomEvent).detail;
      if (!detail) {
        throw new PairingChannelError('INVALID_MESSAGE');
      }
      const { data: payload, sender } = detail;

      if (!payload || typeof payload.message !== 'string') {
        throw new PairingChannelError('INVALID_MESSAGE');
      }

      // Validate sender metadata exists (matches Backbone's PAIRING_MESSAGE_SENDER_SCHEMA)
      if (!sender || typeof sender !== 'object') {
        throw new PairingChannelError('INVALID_MESSAGE');
      }

      const { data = {}, message } = payload;

      console.info(`Receiving pairing message`, message);

      // Enrich with sender metadata (same shape as Backbone)
      data.remoteMetaData = {
        city: sender?.city,
        country: sender?.country,
        region: sender?.region,
        ua: sender?.ua,
        ipAddress: sender?.remote,
      };

      this.dispatchEvent(
        new CustomEvent(`remote:${message}`, { detail: data })
      );
    } catch (err) {
      sentryMetrics.captureException(err);
      this.dispatchEvent(new CustomEvent('error', { detail: err }));
    }
  };

  private handleError = (event: Event) => {
    if (this.unloading) {
      return;
    }
    this.captureWithCloseReason((event as CustomEvent).detail);
    this.dispatchEvent(
      new CustomEvent('error', {
        detail: new PairingChannelError('UNEXPECTED_ERROR'),
      })
    );
  };

  private handleClose = () => {
    if (this.channel) {
      const ch = this.channel;
      this.channel = null;
      this.removeChannelListeners(ch);
    }
    this.detachSocket();
    if (this.unloading) {
      return;
    }
    this.dispatchEvent(new CustomEvent('close'));
  };

  private attachChannel(channel: PairingChannelSocket): void {
    channel.addEventListener('message', this.handleMessage);
    channel.addEventListener('error', this.handleError);
    channel.addEventListener('close', this.handleClose);
  }

  private removeChannelListeners(ch: PairingChannelSocket): void {
    ch.removeEventListener('message', this.handleMessage);
    ch.removeEventListener('error', this.handleError);
    ch.removeEventListener('close', this.handleClose);
  }

  private captureWithCloseReason(err: Error): void {
    const reason = this.socket?.closeReason;
    if (reason) {
      sentryMetrics.captureException(err, { 'pairing.close_reason': reason });
    } else {
      sentryMetrics.captureException(err);
    }
  }

  private handleResumed = (event: Event) => {
    if (this.unloading) {
      return;
    }
    sentryMetrics.captureMessage('Pairing socket resumed', 'info', {
      'pairing.resume_attempts': (event as CustomEvent<{ attempts: number }>)
        .detail.attempts,
    });
  };

  private attachSocket(socket?: ResumableWebSocket): void {
    this.socket = socket;
    socket?.addEventListener('resumed', this.handleResumed);
  }

  private detachSocket(): void {
    this.socket?.removeEventListener('resumed', this.handleResumed);
    this.socket = undefined;
  }

  private handleUnload = () => {
    this.unloading = true;
  };

  private handlePageShow = () => {
    this.unloading = false;
  };

  private watchPageLifecycle(): void {
    this.unloading = false;
    window.addEventListener('beforeunload', this.handleUnload);
    window.addEventListener('pagehide', this.handleUnload);
    window.addEventListener('pageshow', this.handlePageShow);
  }

  private unwatchPageLifecycle(): void {
    window.removeEventListener('beforeunload', this.handleUnload);
    window.removeEventListener('pagehide', this.handleUnload);
    window.removeEventListener('pageshow', this.handlePageShow);
    this.unloading = false;
  }
}
